import { docEntityBaseNew } from "building-types/entities/docEntityBaseNew";
import type { BuiltInLabel } from "building-types/entities/labelTypes";
import type { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import type { PlanViewLevelMember, View3D, PlanView, ElevationView, SectionView } from "building-types/views/View3D";
import type { ViewFilter } from "building-types/views/viewFilterTypes";
import type { PartialRecord } from "common/utils/types";
import uid from "common/utils/uid";

export function view3DNew(
  id: string,
  name: string,
  levelId: string | null,
  sortIndex: number,
  viewFilter: Partial<ViewFilter> = {},
): View3D {
  return {
    ...docEntityBaseNew("VIEW_3D"),
    id,
    name,
    levelId,
    sortIndex,
    viewFilter,
  };
}

export function planViewNew(
  id: string,
  name: string,
  levelIds: readonly PlanViewLevelMember[],
  referenceType: BuiltinPlanViewType,
  sortIndex: number,
  viewFilter: Partial<ViewFilter> = {},
): PlanView {
  return {
    ...docEntityBaseNew("PLAN_VIEW"),
    id,
    name,
    levelIds,
    referenceType,
    sortIndex,
    viewFilter,
  };
}

export function elevationViewNew(
  id: string,
  name: string,
  referenceFrame: ElevationReferenceFrame,
  sortIndex: number,
): ElevationView {
  const labels: PartialRecord<BuiltInLabel, true> = { addToExteriorElevation: true };
  return {
    ...docEntityBaseNew("ELEVATION_VIEW"),
    referenceFrame,
    id,
    name,
    sortIndex,
    viewFilter: { labels },
  };
}

export const DEFAULT_SECTION_VIEW_HEIGHT = 300;
export const DEFAULT_SECTION_VIEW_PADDING = 60;
export const DEFAULT_SECTION_VIEW_BOTTOM = -36;

export interface SectionViewGeometry {
  readonly startX: number;
  readonly startY: number;
  readonly startZ: number;
  readonly endX: number;
  readonly endY: number;
  readonly endZ: number;
  readonly depth?: number;
}
/**
 * A section view is a cross-sectional 2D view that is defined by a start and end point in 3D space.
 * The start point defines the left corner, the end point the right corner. The height of
 * the section view is defined by the lower of startZ and endZ. Together, these two points define
 * a vertical plane in 3D space. The near plane of the camera is defined by this plane and the depth
 * defines the far plane of the camera. Geometry beyond the far plane should be omitted. Omitting the
 * depth implies an unlimited depth.
 */

export function sectionViewNew({
  id = uid(),
  name = "Section View",
  sortIndex = 0,
  startX = 0,
  startY = 0,
  startZ = 0,
  endX = 0,
  endY = 0,
  endZ = 0,
  depth,
  viewFilter = {},
}: {
  id?: string;
  name?: string;
  sortIndex?: number;
  startX?: number;
  startY?: number;
  startZ?: number;
  endX?: number;
  endY?: number;
  endZ?: number;
  depth?: number;
  viewFilter?: Partial<ViewFilter>;
}): SectionView {
  return {
    ...docEntityBaseNew("SECTION_VIEW"),
    id,
    name,
    sortIndex,
    startX,
    startY,
    startZ,
    endX,
    endY,
    endZ,
    depth,
    viewFilter,
    calloutLabels: {},
  };
}
